'use client'

import type {
  CrossTabNotifyOptions,
  CrossTabNotifyResult,
} from '@maestro/commerce-blocks/hooks/hooks.types'

import { useCallback, useEffect, useRef } from 'react'

import { createLogger } from '@maestro/core/logger'

const log = createLogger('maestro:commerce-blocks:cross-tab-notify')

const isBrowser = (): boolean => typeof window !== 'undefined'

const isBroadcastChannelAvailable = (): boolean =>
  isBrowser() && 'BroadcastChannel' in window

/**
 * Cross-tab notification hook.
 *
 * Uses BroadcastChannel API when available, falling back to
 * localStorage `storage` event. Both APIs only fire in other tabs,
 * never in the tab that sent the notification.
 */
export const useCrossTabNotify = (
  props: CrossTabNotifyOptions
): CrossTabNotifyResult => {
  const { channel, onNotified } = props
  const onNotifiedRef = useRef(onNotified)
  useEffect(() => {
    onNotifiedRef.current = onNotified
  }, [onNotified])

  const broadcastChannelRef = useRef<BroadcastChannel | null>(null)

  useEffect(() => {
    if (isBroadcastChannelAvailable()) {
      const bc = new BroadcastChannel(channel)
      bc.onmessage = () => {
        log.debug('Received BroadcastChannel message', { channel })
        onNotifiedRef.current()
      }
      broadcastChannelRef.current = bc

      return () => {
        bc.close()
        broadcastChannelRef.current = null
      }
    }

    // Fallback: storage event (only fires in other tabs)
    const storageKey = `cross_tab_notify:${channel}`
    const handler = (event: StorageEvent) => {
      if (event.key === storageKey) {
        log.debug('Received storage event notification', { channel })
        onNotifiedRef.current()
      }
    }

    window.addEventListener('storage', handler)
    return () => window.removeEventListener('storage', handler)
  }, [channel])

  const notify = useCallback(() => {
    if (!isBrowser()) {
      return
    }

    if (broadcastChannelRef.current) {
      log.debug('Sending BroadcastChannel notification', { channel })
      broadcastChannelRef.current.postMessage('notify')
      return
    }

    // Fallback: write to localStorage to trigger storage event in other tabs
    log.debug('Sending localStorage notification', { channel })
    try {
      const storageKey = `cross_tab_notify:${channel}`
      localStorage.setItem(storageKey, Date.now().toString())
    } catch {
      log.debug('Failed to notify other tabs via localStorage')
    }
  }, [channel])

  return { notify }
}
